import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import { withNamespaces } from "react-i18next";
import moment from "moment";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

import LazyImage from "../../../../Utility/Components/LazyImageComponent";
import SkeletonThemeContentGroupList from "../../../../Utility/Components/SkeletonThemeContentGroupList";
import Utility from "../../../../Utility/Utility";
import GroupPackageSearchPanel from "../Components/GroupPackageSearchPanel";
import BreadcrumbMenu from "../../../../Utility/Components/BreadcrumbMenu";

import { getGroupTourApi } from "../../../../GlobalModules/Login/Controller/LoginController";
import {
    loaderStateTrue,
    loaderStateFalse,
    tourSearchData,
    groupTourPackageSearchData,
} from "../../../../../src/Actions/AllAction";

import "../../../../Utility/Public/css/common.scss";
import "../Assets/css/homedoc.scss";
import "../Assets/css/homeresponsivedoc.scss";
import "../Assets/css/tour-list-modern.scss";

const FALLBACK_IMAGE = require("../../../../Utility/Public/images/stateLoaderImage.png");

const DepartureModalSkeleton = () => (
    <SkeletonTheme baseColor="#f3f3f3" highlightColor="#ebebeb">
        <div className="departure-skeleton-list">
            {Array.from({ length: 5 }).map((_, i) => (
                <div className="departure-skeleton-row" key={i}>
                    <Skeleton circle width={18} height={18} />
                    <div className="departure-skeleton-info">
                        <Skeleton width="70%" height={13} />
                        <Skeleton width="45%" height={10} style={{ marginTop: 6 }} />
                    </div>
                    <div className="departure-skeleton-price">
                        <Skeleton width={60} height={13} />
                        <Skeleton width={40} height={9} style={{ marginTop: 6 }} />
                    </div>
                    <Skeleton width={70} height={20} borderRadius={999} />
                </div>
            ))}
        </div>
    </SkeletonTheme>
);

const TourCardSkeleton = () => {
    const count = 8;
    return (
        <SkeletonTheme baseColor="#f3f3f3" highlightColor="#ebebeb">
            <div className="modern-tour-grid">
                {Array.from({ length: count }).map((_, i) => (
                    <article className="modern-tour-card tour-card-skeleton" key={i}>
                        <div className="tour-card-image skeleton-image">
                            <Skeleton width="100%" height="100%" />
                        </div>

                        <div className="tour-card-body">
                            <div className="tour-card-heading">
                                <Skeleton width="80%" height={16} />
                                <div className="tour-rating-row">
                                    <Skeleton width={60} height={12} style={{ marginTop: 8 }} />
                                </div>
                            </div>

                            <div className="tour-feature-row">
                                <Skeleton width={70} height={10} />
                                <Skeleton width={70} height={10} />
                                <Skeleton width={70} height={10} />
                            </div>

                            <div className="tour-card-bottom">
                                <div className="tour-next-date">
                                    <Skeleton width={90} height={10} style={{ marginBottom: 6 }} />
                                    <Skeleton width={110} height={12} />
                                </div>
                                <div className="tour-price">
                                    <Skeleton width={70} height={10} style={{ marginBottom: 4 }} />
                                    <Skeleton width={80} height={18} />
                                </div>
                            </div>

                            <div className="tour-card-actions single-action">
                                <Skeleton width="100%" height={41} borderRadius={7} />
                            </div>
                        </div>
                    </article>
                ))}
            </div>
        </SkeletonTheme>
    );
};

const TourList = (props) => {
    const history = useHistory();
    const location = useLocation();

    const itemsPerPage = 8;

    const [currentPage, setCurrentPage] = useState(1);
    const [groupTourState, setGroupTourState] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [apiCallFinish, setApiCallFinish] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const [selectedDepartures, setSelectedDepartures] = useState({});
    const [departureModalTour, setDepartureModalTour] = useState(null);
    const [modalSelectedDepartureId, setModalSelectedDepartureId] = useState(null);
    const [modalDepartures, setModalDepartures] = useState([]);
    const [isLoadingDepartures, setIsLoadingDepartures] = useState(false);

    const [sortBy, setSortBy] = useState("recommended");
    const [viewMode, setViewMode] = useState("grid");

    const initialLoadRef = useRef(true);
    const urlProcessedRef = useRef(false);
    const lastFetchRef = useRef(null);

    const totalPages = Math.max(1, Math.ceil(totalRecords / itemsPerPage));

    /* -------------------------------------------------------------------------- */
    /* Helpers                                                                    */
    /* -------------------------------------------------------------------------- */

    const getTourKey = (tour) =>
        String(
            tour?.tour_id ||
            tour?.id ||
            tour?.tour_code
        );

    const isPastDeparture = (departure) => {
        if (!departure?.start_date) return false;
        return moment(departure.start_date).isBefore(moment().startOf("day"), "day");
    };

    const getValidDepartures = (tour) => {
        const list = Array.isArray(tour?.departures) ? tour.departures : [];

        return list
            .filter((departure) => departure?.start_date && departure?.end_date)
            .sort(
                (a, b) =>
                    moment(a.start_date, "YYYY-MM-DD").valueOf() -
                    moment(b.start_date, "YYYY-MM-DD").valueOf()
            );
    };

    const getSelectedDeparture = (tour) => {
        const departures = getValidDepartures(tour);
        if (!departures.length) return tour;

        const tourKey = getTourKey(tour);
        const selectedId = selectedDepartures[tourKey];

        return (
            departures.find(
                (departure) =>
                    String(departure.id) === String(selectedId) ||
                    String(departure.tour_code) === String(selectedId)
            ) || departures[0]
        );
    };

    const getTourImage = (tour) => {
        if (tour?.thumbnail_path) return tour.thumbnail_path;

        const media = Array.isArray(tour?.Media) ? tour.Media : [];
        if (media.length && media[0]?.file_url) {
            try {
                const parsed = JSON.parse(media[0].file_url);

                if (Array.isArray(parsed) && parsed.length) {
                    return parsed[0]?.file_url || parsed[0]?.file_obj || FALLBACK_IMAGE;
                }

                if (parsed?.file_url || parsed?.file_obj) {
                    return parsed.file_url || parsed.file_obj;
                }
            } catch (error) {
                if (
                    typeof media[0].file_url === "string" &&
                    media[0].file_url.startsWith("http")
                ) {
                    return media[0].file_url;
                }
            }
        }

        try {
            const destinationImage = tour?.DestinationTo?.image_url;
            if (destinationImage) {
                const parsed = JSON.parse(destinationImage);
                return parsed?.file_obj || parsed?.file_url || FALLBACK_IMAGE;
            }
        } catch (error) {
            // no-op
        }

        return FALLBACK_IMAGE;
    };

    const getCurrency = (tour) =>
        tour?.branch_details?.currency || tour?.base_currency || "INR";

    const getPrice = (tour) => {
        const departure = getSelectedDeparture(tour);

        return (
            departure?.price_for_adult ||
            tour?.price_for_adult ||
            departure?.final_selling_price ||
            tour?.final_selling_price ||
            0
        );
    };

    const formatPrice = (tour) => {
        const symbol = Utility.getCurrencySymbol(getCurrency(tour));
        const amount = Number(getPrice(tour) || 0);

        return `${symbol}${amount.toLocaleString("en-IN", {
            maximumFractionDigits: 0,
        })}`;
    };

    const getDuration = (tour) => {
        const departure = getSelectedDeparture(tour);

        if (departure?.duration) return departure.duration;
        if (tour?.duration) return tour.duration;

        const nights = departure?.duration_nights ?? tour?.duration_nights;
        const days = departure?.duration_days ?? tour?.duration_days;

        if (nights != null || days != null) {
            return `${nights || 0}N / ${days || 0}D`;
        }

        return "Tour";
    };

    const normalizeFeatures = (tour) => {
        const inclusions = Array.isArray(tour?.inclusions)
            ? tour.inclusions
            : Array.isArray(tour?.Inclusions)
                ? tour.Inclusions
                : [];

        const text = inclusions
            .map((item) =>
                typeof item === "string" ? item : item?.inclusion_text || ""
            )
            .join(" ")
            .toLowerCase();

        const itineraryText = JSON.stringify(
            tour?.Itineraries || tour?.itinerary_json || []
        ).toLowerCase();

        return [
            {
                icon: "fa-users",
                label:
                    String(tour?.tour_type || "").toLowerCase() === "package"
                        ? "Package Tour"
                        : "Group Tour",
            },
            {
                icon: "fa-cutlery",
                label: text.includes("breakfast") || text.includes("meal") ? "Meals" : "Meals",
            },
            {
                icon: "fa-bus",
                label:
                    text.includes("transport") ||
                        itineraryText.includes("transfer") ||
                        itineraryText.includes("flight")
                        ? "Transport"
                        : "Transport",
            },
            {
                icon: "fa-camera",
                label:
                    itineraryText.includes("activity") ||
                        itineraryText.includes("visit") ||
                        itineraryText.includes("sightseeing")
                        ? "Sightseeing"
                        : "Sightseeing",
            },
        ];
    };

    const getAvailability = (departure) => {
        if (!departure) {
            return { label: "Available", className: "available" };
        }

        if (departure.is_sold_out) {
            return { label: "Sold out", className: "sold-out" };
        }

        const seats = Number(departure.remaining_guest_count);
        if (departure.is_filling_fast) {
            return {
                label: seats > 0 ? `Only ${seats} seats left` : "Filling fast",
                className: "filling-fast",
            };
        }

        if (Number.isFinite(seats) && seats > 0 && seats <= 5) {
            return {
                label: `Only ${seats} seats left`,
                className: "low-stock",
            };
        }

        return { label: "Available", className: "available" };
    };

    /* -------------------------------------------------------------------------- */
    /* URL                                                                        */
    /* -------------------------------------------------------------------------- */

    const buildTourUrlParams = (
        searchData = props.userGroupTourPackageSearchData || {},
        page = currentPage
    ) => {
        const searchParams = new URLSearchParams();

        if (searchData.starting_from?.value) {
            searchParams.set("from", searchData.starting_from.value);
        }

        if (searchData.going_to?.value) {
            searchParams.set("to", searchData.going_to.value);
        }

        if (searchData.search) {
            searchParams.set("search", searchData.search);
        }

        if (searchData.start_date) {
            searchParams.set(
                "start_date",
                moment(searchData.start_date).format("YYYY-MM-DD")
            );
        }

        if (searchData.end_date) {
            searchParams.set(
                "end_date",
                moment(searchData.end_date).format("YYYY-MM-DD")
            );
        }

        if (page > 1) {
            searchParams.set("page", String(page));
        }

        return searchParams.toString();
    };

    const updateUrl = (page = currentPage) => {
        const params = buildTourUrlParams(
            props.userGroupTourPackageSearchData || {},
            page
        );
        const nextUrl = `${history.location.pathname}${params ? `?${params}` : ""}`;

        if (`${location.pathname}${location.search}` !== nextUrl) {
            history.replace(nextUrl);
        }
    };

    const parseUrlParams = () => {
        if (urlProcessedRef.current) return;

        const searchParams = new URLSearchParams(location.search);

        const from = searchParams.get("from");
        const to = searchParams.get("to");
        const search = searchParams.get("search");
        const startDate = searchParams.get("start_date");
        const endDate = searchParams.get("end_date");
        const page = Number(searchParams.get("page") || 1);

        const nextSearchData = {
            ...(props.userGroupTourPackageSearchData || {}),
        };

        let changed = false;

        if (from) {
            nextSearchData.starting_from = { value: from, label: from };
            changed = true;
        }

        if (to) {
            nextSearchData.going_to = { value: to, label: to };
            changed = true;
        }

        if (search) {
            nextSearchData.search = search;
            changed = true;
        }

        if (startDate) {
            nextSearchData.start_date = new Date(startDate);
            changed = true;
        }

        if (endDate) {
            nextSearchData.end_date = new Date(endDate);
            changed = true;
        }

        if (changed) {
            props.groupTourPackageSearchData(nextSearchData);
        }

        setCurrentPage(Number.isFinite(page) && page > 0 ? page : 1);
        urlProcessedRef.current = true;
    };

    /* -------------------------------------------------------------------------- */
    /* Group same package departures                                              */
    /* -------------------------------------------------------------------------- */

    const groupToursData = (tours) => {
        if (!Array.isArray(tours)) return [];

        const groups = {};

        tours.forEach((tour) => {
            const groupKey = String(
                tour.parent_tour_detail_id ||
                tour.tour_series_group_code ||
                tour.id
            );

            if (!groups[groupKey]) {
                groups[groupKey] = {
                    tourInfo: tour,
                    departures: [],
                };
            }

            const isParent =
                tour.is_parent_tour === true ||
                String(tour.is_parent_tour).toLowerCase() === "true" ||
                !tour.parent_tour_detail_id;

            if (isParent) {
                groups[groupKey].tourInfo = tour;
            }

            groups[groupKey].departures.push(tour);
        });

        return Object.values(groups).map((group) => ({
            ...group.tourInfo,
            departures: group.departures.sort((a, b) => {
                if (!a?.start_date) return 1;
                if (!b?.start_date) return -1;
                return moment(a.start_date).valueOf() - moment(b.start_date).valueOf();
            }),
        }));
    };

    /* -------------------------------------------------------------------------- */
    /* API                                                                        */
    /* -------------------------------------------------------------------------- */

    const getGroupTour = (page = 1, freshData = null) => {
        const searchData =
            freshData || props.userGroupTourPackageSearchData || {};

        const from = searchData.starting_from?.value?.trim() || "";
        const to = searchData.going_to?.value?.trim() || "";
        const search = searchData.search?.trim() || "";
        const startDate = searchData.start_date
            ? moment(searchData.start_date).format("YYYY-MM-DD")
            : "";
        const endDate = searchData.end_date
            ? moment(searchData.end_date).format("YYYY-MM-DD")
            : "";
        const fetchSignature = JSON.stringify({
            from,
            to,
            search,
            startDate,
            endDate,
            page,
        });

        if (lastFetchRef.current === fetchSignature) {
            return;
        }
        lastFetchRef.current = fetchSignature;

        const filterHash = {
            tour_type: "group,package",
            offset: (page - 1) * itemsPerPage,
            limit: itemsPerPage,
        };

        if (searchData.starting_from?.value?.trim()) {
            filterHash.location = searchData.starting_from.value.trim();
        }

        if (searchData.going_to?.value?.trim()) {
            filterHash.to_location = searchData.going_to.value.trim();
        }

        if (searchData.search?.trim()) {
            filterHash.search = searchData.search.trim();
        }

        if (searchData.start_date) {
            filterHash.start_date = moment(searchData.start_date).format("YYYY-MM-DD");
        }

        if (searchData.end_date) {
            filterHash.end_date = moment(searchData.end_date).format("YYYY-MM-DD");
        }

        const filters = {
            filters: JSON.stringify(filterHash),
        };

        setIsLoading(true);
        props.loaderStateTrue(true);

        getGroupTourApi(filters)
            .then((response) => {
                if (response?.success) {
                    const rawData = Array.isArray(response.data) ? response.data : [];
                    setGroupTourState(rawData);

                    const total =
                        Number(response.total) ||
                        rawData.length;

                    setTotalRecords(total);
                } else {
                    setGroupTourState([]);
                    setTotalRecords(0);
                }

                setApiCallFinish(true);
            })
            .catch((error) => {
                console.error("Group tour API error:", error);
                setGroupTourState([]);
                setTotalRecords(0);
                setApiCallFinish(true);
            })
            .finally(() => {
                setIsLoading(false);
                props.loaderStateFalse(false);
            });
    };

    const callGetGroupTour = (page = 1, freshData = null) => {
        setCurrentPage(page);
        updateUrl(page);
        getGroupTour(page, freshData);
    };

    /* -------------------------------------------------------------------------- */
    /* Navigation                                                                 */
    /* -------------------------------------------------------------------------- */

    const goToTourDetails = (tour, forcedDeparture = null) => {
        const selectedDeparture = forcedDeparture || getSelectedDeparture(tour);

        const reducerData = props.tourSearchDataFromReducer || {};
        const tempSearchData = {
            ...reducerData,
            ...tour,
            ...selectedDeparture,
            departures: tour.departures || [],
        };

        props.tourSearchData(tempSearchData);

        const urlParams = buildTourUrlParams(
            props.userGroupTourPackageSearchData || {}
        );

        const tourCode = selectedDeparture?.tour_code || tour?.tour_code;
        const lang = localStorage.getItem("i18nextLng") || "en";
        window.scrollTo({ top: 0, behavior: "smooth" });
        history.push(
            `/${lang}/tour-details/${tourCode}${urlParams ? `?${urlParams}` : ""}`
        );
    };

    const openDepartureModal = async (tour) => {
        const localDepartures = getValidDepartures(tour);
        const hasMultiple =
            Number(tour?.total_tour) > 1 || localDepartures.length > 1;

        if (!hasMultiple) {
            const departure = localDepartures[0];
            if (departure && !isPastDeparture(departure)) {
                goToTourDetails(tour, departure);
            }
            return;
        }

        const tourKey = getTourKey(tour);
        const previousSelected = selectedDepartures[tourKey];
        const currentSelected =
            previousSelected &&
                localDepartures.some(
                    (departure) =>
                        String(departure.id) === String(previousSelected) &&
                        !isPastDeparture(departure)
                )
                ? previousSelected
                : null;

        setDepartureModalTour(tour);
        setModalSelectedDepartureId(currentSelected);
        setModalDepartures([]);
        setIsLoadingDepartures(true);

        const filterHash = {
            tour_type: "group,package",
            tour_id: tour.tour_id
        };

        try {
            const response = await getGroupTourApi({
                filters: JSON.stringify(filterHash),
            });
            if (response?.success) {
                const rawData = Array.isArray(response.data) ? response.data : [];
                setModalDepartures(getValidDepartures({ departures: rawData }));
            } else {
                setModalDepartures(getValidDepartures({ departures: [tour] }));
            }
        } catch (error) {
            console.error("Error fetching tour departures:", error);
            setModalDepartures(getValidDepartures({ departures: [tour] }));
        } finally {
            setIsLoadingDepartures(false);
        }
    };

    const closeDepartureModal = () => {
        setDepartureModalTour(null);
        setModalSelectedDepartureId(null);
        setModalDepartures([]);
        setIsLoadingDepartures(false);
    };

    const continueWithSelectedDeparture = () => {
        if (!departureModalTour) return;

        const selected = modalDepartures.find(
            (departure) =>
                String(departure.id) === String(modalSelectedDepartureId) ||
                String(departure.tour_code) === String(modalSelectedDepartureId)
        );

        if (!selected || isPastDeparture(selected)) return;

        const tourKey = getTourKey(departureModalTour);

        setSelectedDepartures((previous) => ({
            ...previous,
            [tourKey]: selected.id || selected.tour_code,
        }));

        closeDepartureModal();
        goToTourDetails(departureModalTour, selected);
    };

    /* -------------------------------------------------------------------------- */
    /* Effects                                                                    */
    /* -------------------------------------------------------------------------- */

    useEffect(() => {
        parseUrlParams();

        const params = new URLSearchParams(location.search);
        const initialPage = Number(params.get("page") || 1);

        setCurrentPage(initialPage);
        getGroupTour(initialPage);

        initialLoadRef.current = false;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filterValues = useMemo(() => {
        const searchData = props.userGroupTourPackageSearchData || {};

        return {
            from: searchData.starting_from?.value?.trim() || "",
            to: searchData.going_to?.value?.trim() || "",
            search: searchData.search?.trim() || "",
            startDate: searchData.start_date
                ? moment(searchData.start_date).format("YYYY-MM-DD")
                : "",
            endDate: searchData.end_date
                ? moment(searchData.end_date).format("YYYY-MM-DD")
                : "",
        };
    }, [props.userGroupTourPackageSearchData]);

    useEffect(() => {
        if (initialLoadRef.current) return;

        setCurrentPage(1);
        updateUrl(1);

        const timer = setTimeout(() => {
            getGroupTour(1);
        }, 250);

        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        filterValues.from,
        filterValues.to,
        filterValues.search,
        filterValues.startDate,
        filterValues.endDate,
    ]);

    useEffect(() => {
        if (initialLoadRef.current) return;
        updateUrl(currentPage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage]);

    useEffect(() => {
        const handleEscape = (event) => {
            if (event.key === "Escape") closeDepartureModal();
        };

        document.addEventListener("keydown", handleEscape);
        return () => document.removeEventListener("keydown", handleEscape);
    }, []);

    useEffect(() => {
        if (!departureModalTour) return;
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = originalOverflow;
        };
    }, [departureModalTour]);

    /* -------------------------------------------------------------------------- */
    /* Sort                                                                       */
    /* -------------------------------------------------------------------------- */

    const visibleTours = useMemo(() => {
        const copy = [...groupTourState];

        if (sortBy === "price-low") {
            return copy.sort((a, b) => Number(getPrice(a)) - Number(getPrice(b)));
        }

        if (sortBy === "price-high") {
            return copy.sort((a, b) => Number(getPrice(b)) - Number(getPrice(a)));
        }

        if (sortBy === "departure") {
            return copy.sort((a, b) => {
                const aDate = getValidDepartures(a)[0]?.start_date;
                const bDate = getValidDepartures(b)[0]?.start_date;

                if (!aDate) return 1;
                if (!bDate) return -1;

                return moment(aDate).valueOf() - moment(bDate).valueOf();
            });
        }

        return copy;
    }, [groupTourState, sortBy, selectedDepartures]);

    /* -------------------------------------------------------------------------- */
    /* Render                                                                     */
    /* -------------------------------------------------------------------------- */

    return (
        <div className="modern-tour-page">
            <div className="modern-tour-shell container">
                <section className="modern-search-wrap">
                    <GroupPackageSearchPanel
                        getGroupTour={callGetGroupTour}
                        setGroupTourState={setGroupTourState}
                        callFrom="tour-list"
                    />
                </section>
                <section className="tour-breadcrumb">
                    <BreadcrumbMenu
                        homeMenuDisplay={true}
                        tourListDisplay={true}
                        activeTourList={true}
                        homeMenuClick={() => history.push(`/${localStorage.getItem('i18nextLng')}/home`)}
                        tourListClick={() => { }}
                    />
                </section>
                <section className="modern-tour-hero">
                    <div>
                        <h1>Group / Package Tours</h1>
                        <p>
                            Join a group departure or package tour and explore the best
                            destinations with expert planning.
                        </p>
                    </div>
                    <div className="hero-count">
                        Showing <strong>{groupTourState.length}</strong> tours
                    </div>
                </section>

                {isLoading ? (
                    <TourCardSkeleton />
                ) : apiCallFinish && visibleTours.length > 0 ? (
                    <>
                        <div
                            className={`modern-tour-grid ${viewMode === "list" ? "list-view" : ""
                                }`}
                        >
                            {visibleTours.map((tour) => {
                                const tourKey = getTourKey(tour);
                                const imageUrl = getTourImage(tour);
                                const departures = getValidDepartures(tour);
                                const selectedDeparture = getSelectedDeparture(tour);
                                const features = normalizeFeatures(tour);
                                const nextDeparture =
                                    selectedDeparture?.start_date || tour?.start_date;

                                const category =
                                    tour?.CategoryData?.category_name ||
                                    tour?.package_category ||
                                    "Tour";

                                const isPopular =
                                    Number(selectedDeparture?.filling_percentage || 0) >= 50 ||
                                    selectedDeparture?.is_filling_fast;
                                const totalDepartures = Number(tour?.total_tour || 0);
                                const hasMultipleDepartures = totalDepartures > 1;

                                return (
                                    <article className="modern-tour-card" key={tourKey}>
                                        <div className="tour-card-image">
                                            <LazyImage
                                                src={imageUrl}
                                                alt={tour?.tour_name || tour?.template_name || "Tour"}
                                                loaderImage={FALLBACK_IMAGE}
                                            />

                                            <span
                                                className={`tour-card-badge ${isPopular ? "popular" : ""
                                                    }`}
                                            >
                                                {isPopular ? "Popular" : category}
                                            </span>

                                            {/* <button
                                                type="button"
                                                className="tour-wishlist"
                                                aria-label="Save tour"
                                                onClick={(event) => event.stopPropagation()}
                                            >
                                                <i className="fa fa-heart-o" />
                                            </button> */}

                                            <span className="tour-duration">{getDuration(tour)}</span>

                                            {hasMultipleDepartures && (
                                                <button
                                                    type="button"
                                                    className="tour-departure-count"
                                                    onClick={() => openDepartureModal(tour)}
                                                >
                                                    <i className="fa fa-calendar-o" />
                                                    {totalDepartures} departures available
                                                </button>
                                            )}
                                        </div>

                                        <div className="tour-card-body">
                                            <div className="tour-card-heading">
                                                <h3 title={tour?.tour_name || tour?.template_name}>
                                                    {tour?.tour_name ||
                                                        tour?.template_name ||
                                                        "Tour Package"}
                                                </h3>

                                                <div className="tour-rating-row">
                                                    <span className="tour-rating">
                                                        <i className="fa fa-star" /> 4.6
                                                    </span>
                                                    <span className="tour-rating-note">
                                                        Curated tour experience
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="tour-feature-row">
                                                {features.map((feature) => (
                                                    <span key={feature.label}>
                                                        <i className={`fa ${feature.icon}`} />
                                                        {feature.label}
                                                    </span>
                                                ))}
                                            </div>

                                            <div className="tour-card-bottom">
                                                <div className="tour-next-date">
                                                    <small>
                                                        {hasMultipleDepartures
                                                            ? "Upcoming Departure"
                                                            : "Departure"}
                                                    </small>

                                                    <strong>
                                                        <i className="fa fa-calendar-o" />
                                                        {nextDeparture
                                                            ? moment(nextDeparture).format("DD MMM YYYY")
                                                            : "Flexible Date"}
                                                    </strong>

                                                    {hasMultipleDepartures && (
                                                        <button
                                                            type="button"
                                                            className="change-date-link"
                                                            onClick={() => openDepartureModal(tour)}
                                                        >
                                                            View all dates
                                                        </button>
                                                    )}
                                                </div>

                                                <div className="tour-price">
                                                    <small>From</small>
                                                    <strong>{formatPrice(tour)}</strong>
                                                    <span>per person</span>
                                                </div>
                                            </div>

                                            <div className={`tour-card-actions ${hasMultipleDepartures ? '' : 'single-action'}`}>
                                                <button
                                                    type="button"
                                                    className={`tour-btn tour-btn-outline ${hasMultipleDepartures ? '' : 'tour-btn-full'}`}
                                                    onClick={() => goToTourDetails(tour)}
                                                >
                                                    View Details
                                                </button>

                                                {hasMultipleDepartures && (
                                                    <button
                                                        type="button"
                                                        className="tour-btn tour-btn-primary"
                                                        onClick={() => openDepartureModal(tour)}
                                                    >
                                                        Choose Date
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </article>
                                );
                            })}
                        </div>

                        {totalPages > 1 && (
                            <div className="modern-pagination">
                                <button
                                    type="button"
                                    className="pagination-arrow"
                                    disabled={currentPage <= 1}
                                    onClick={() => {
                                        const page = Math.max(1, currentPage - 1);
                                        setCurrentPage(page);
                                        getGroupTour(page);
                                    }}
                                >
                                    <i className="fa fa-angle-left" />
                                </button>

                                {Array.from({ length: totalPages }, (_, index) => index + 1)
                                    .filter(
                                        (page) =>
                                            page === 1 ||
                                            page === totalPages ||
                                            Math.abs(page - currentPage) <= 1
                                    )
                                    .reduce((acc, page, index, pages) => {
                                        if (index > 0 && page - pages[index - 1] > 1) {
                                            acc.push(
                                                <span
                                                    className="pagination-ellipsis"
                                                    key={`ellipsis-${page}`}
                                                >
                                                    ...
                                                </span>
                                            );
                                        }

                                        acc.push(
                                            <button
                                                type="button"
                                                key={page}
                                                className={currentPage === page ? "active" : ""}
                                                onClick={() => {
                                                    setCurrentPage(page);
                                                    getGroupTour(page);
                                                }}
                                            >
                                                {page}
                                            </button>
                                        );

                                        return acc;
                                    }, [])}

                                <button
                                    type="button"
                                    className="pagination-arrow"
                                    disabled={currentPage >= totalPages}
                                    onClick={() => {
                                        const page = Math.min(totalPages, currentPage + 1);
                                        setCurrentPage(page);
                                        getGroupTour(page);
                                    }}
                                >
                                    <i className="fa fa-angle-right" />
                                </button>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="modern-tour-empty">
                        <span>
                            <i className="fa fa-map-o" />
                        </span>
                        <h3>No tours found</h3>
                        <p>Try changing your destination or travel dates.</p>
                    </div>
                )}
            </div>

            {departureModalTour && (
                <div
                    className="departure-modal-backdrop"
                    role="presentation"
                    onMouseDown={(event) => {
                        if (event.target === event.currentTarget) closeDepartureModal();
                    }}
                >
                    <div
                        className="departure-modal"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="departure-modal-title"
                    >
                        <div className="departure-modal-header">
                            <div>
                                <span className="departure-modal-kicker">
                                    Select your travel date
                                </span>
                                <h3 id="departure-modal-title">
                                    Choose Departure Date
                                </h3>
                                <p>
                                    {departureModalTour?.DestinationFrom?.destination_name ||
                                        "Origin"}
                                    <i className="fa fa-long-arrow-right" />
                                    {departureModalTour?.DestinationTo?.destination_name ||
                                        departureModalTour?.tour_name ||
                                        "Destination"}
                                    <span>•</span>
                                    {getDuration(departureModalTour)}
                                </p>
                            </div>

                            <button
                                type="button"
                                className="departure-modal-close"
                                onClick={closeDepartureModal}
                                aria-label="Close"
                            >
                                <i className="fa fa-times" />
                            </button>
                        </div>

                        <div className="departure-modal-list">
                            {isLoadingDepartures ? (
                                <DepartureModalSkeleton />
                            ) : modalDepartures.length === 0 ? (
                                <div className="departure-modal-empty">
                                    No departures available.
                                </div>
                            ) : (
                                modalDepartures.map((departure) => {
                                const departureKey = departure.id || departure.tour_code;
                                const isSelected =
                                    String(departure.id) ===
                                    String(modalSelectedDepartureId) ||
                                    String(departure.tour_code) ===
                                    String(modalSelectedDepartureId);
                                const isPast = isPastDeparture(departure);
                                const isDisabled = departure.is_sold_out || isPast;

                                const availability = getAvailability(departure);
                                const symbol = Utility.getCurrencySymbol(
                                    departure?.branch_details?.currency ||
                                    departureModalTour?.branch_details?.currency ||
                                    departure?.base_currency ||
                                    "INR"
                                );

                                const price = Number(
                                    departure?.price_for_adult ||
                                    departure?.final_selling_price ||
                                    0
                                );

                                return (
                                    <div
                                        className={`departure-option ${isSelected ? "selected" : ""
                                            } ${isDisabled ? "disabled" : ""}`}
                                        key={departureKey}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (!isDisabled) {
                                                setModalSelectedDepartureId(departureKey);
                                            }
                                        }}
                                    >
                                        <input
                                            type="radio"
                                            name="tour-departure"
                                            value={departureKey}
                                            checked={isSelected}
                                            disabled={isDisabled}
                                            onChange={() => {
                                                if (!isDisabled) {
                                                    setModalSelectedDepartureId(departureKey);
                                                }
                                            }}
                                        />

                                        <span className="custom-radio" />

                                        <span className="departure-date-info">
                                            <strong>
                                                {moment(departure.start_date).format("DD MMM")} -{" "}
                                                {moment(departure.end_date).format("DD MMM YYYY")}
                                            </strong>
                                            <small>
                                                {departure.tour_code ? `${departure.tour_code} • ` : ""}
                                                {departure.duration ||
                                                    `${departure.duration_nights || 0} Nights / ${departure.duration_days || 0
                                                    } Days`}
                                            </small>
                                        </span>

                                        <span className="departure-option-price">
                                            <strong>
                                                {symbol}
                                                {price.toLocaleString("en-IN", {
                                                    maximumFractionDigits: 0,
                                                })}
                                            </strong>
                                            <small>per person</small>
                                        </span>

                                        <span
                                            className={`departure-status ${availability.className}`}
                                        >
                                            {availability.label}
                                        </span>
                                    </div>
                                );
                            })
                            )}
                        </div>

                        <div className="departure-modal-footer">
                            <div className="modal-help-text">
                                <i className="fa fa-info-circle" />
                                Select a departure to view its exact availability and tour
                                details.
                            </div>

                            <button
                                type="button"
                                className="departure-continue"
                                onClick={continueWithSelectedDeparture}
                                disabled={!modalSelectedDepartureId}
                            >
                                Continue
                                <i className="fa fa-arrow-right" />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const mapStateToProps = (globalState) => ({
    userCredentials: globalState.LoginReducer.userCredentials,
    token: globalState.LoginReducer.token,
    tourSearchDataFromReducer: globalState.mainReducerData.tourSearchData,
    userGroupTourPackageSearchData:
        globalState.mainReducerData.groupTourPackageSearchData,
});

export default connect(mapStateToProps, {
    loaderStateTrue,
    loaderStateFalse,
    tourSearchData,
    groupTourPackageSearchData,
})(withNamespaces()(TourList));